import React, { useState, useEffect } from "react";
import apiClient, { getApiError } from "../apiClient";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useShop } from "../context/ShopContext";
import { FaSpinner } from "react-icons/fa";
import EmptyState from "./EmptyState";
import { fetchSortOrderMap, applySortOrder, buildCategorySegments } from "../utils/sortOrder";

/* ─── Brand colours ──────────────────────────── */
const P   = "#7B1D4F";
const PDK = "#5E1540";
const GRN = "#16A34A";
const ORG = "#EA580C";
const BDR = "#E5E7EB";
const MUT = "#6B7280";
const fmt = n => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const pad      = n => String(n).padStart(2, "0");
const nowD     = new Date();
const timeStr  = nowD.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" });
const todayISO = `${nowD.getFullYear()}-${pad(nowD.getMonth()+1)}-${pad(nowD.getDate())}`;

/* ═══════════════════════════════════════════════ */
export default function DesiStockStatic() {
  const navigate = useNavigate();
  const { userId, companyId } = useShop();

  const [shopId,          setShopId]          = useState(null);
  const [tokenString,     setToken]           = useState(null);
  const [shopName,        setShopName]        = useState(null);
  const [apiConditionMet, setApiConditionMet] = useState(null);

  /* rows — flat list, one per productId */
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const [date,      setDate]      = useState(todayISO);
  const [search,    setSearch]    = useState("");
  const [activeCat, setActiveCat] = useState("ALL");
  const [preview,   setPreview]   = useState(false);

  /* which variant chip is active per product (key = pName) */
  const [selectedVars, setSelectedVars] = useState({});

  const [popupData1, setPopupData1] = useState([]);
  const [popupData2, setPopupData2] = useState([]);

  const showToast = (msg, type = "error") => {
    toast.dismiss();
    type === "success" ? toast.success(msg) : toast.error(msg);
  };

  /* ── Auth ── */
  useEffect(() => {
    const sid   = localStorage.getItem("shopId");
    const token = localStorage.getItem("tokenString");
    const sName = localStorage.getItem("shopName");
    if (sid && token && sName) {
      setShopId(sid); setToken(token); setShopName(sName);
    } else {
      showToast("Please log in first!", "error");
      navigate("/signin");
    }
  }, [navigate]);

  /* ── Check already submitted ── */
  const checkStockData = async () => {
    try {
      const res = await apiClient.post(
        "/Liquor/GetTodayStock",
        { shopId, typeId: "2" }
      );
      setApiConditionMet(res.data?.data === true ? false : true);
    } catch { setApiConditionMet(true); }
  };
  useEffect(() => { if (shopId) checkStockData(); }, [shopId]);

  /* ── Fetch stock ── */
  const fetchStock = async () => {
    if (!shopId || !tokenString) return;
    setLoading(true);
    try {
      const res = await apiClient.post(
        "/Liquor/GetStockProducts",
        { shopId, typeId: "2" }
      );
      const data = res.data.data || [];
      const rawMapped = data.map(p => ({
        id:           p.id,
        /* category — will auto-populate once API returns catName/category.
           For now fallback to "देशी" so the category label always shows. */
        cat:          p.catName || p.category || p.categoryName || p.typeName || "देशी",
        pName:        p.pName,
        packing:      p.packing,
        mrp:          parseFloat(p.mrp)          || 0,
        openingStock: parseFloat(p.openingStock) || 0,
        receivedQty:  parseFloat(p.receivedQty)  || 0,
        transferQty:  parseFloat(p.transferQty)  || 0,
        closing:      "",
      }));
      // Re-sort by the authoritative shop+type sort-order map so this screen agrees
      // with every other parcha rendering surface on the same product sequence.
      const sortMap = await fetchSortOrderMap(shopId, "2");
      const mapped = applySortOrder(rawMapped, sortMap);
      setRows(mapped);

      /* initialise first variant as selected for each product group */
      const initSel = {};
      const seen = new Set();
      mapped.forEach(r => {
        if (!seen.has(r.pName)) { seen.add(r.pName); initSel[r.pName] = r.id; }
      });
      setSelectedVars(initSel);
    } catch (err) {
      showToast("Error fetching stock data", "error");
      console.error(err);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (shopId && tokenString && shopName) fetchStock();
  }, [shopId, tokenString, shopName]);

  /* ── Closing update — by productId ── */
  const handleClosing = (id, val) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      if (val === "") return { ...r, closing: "" };
      const n = parseFloat(val);
      if (isNaN(n) || n < 0) return r;
      const max = r.openingStock + r.receivedQty - r.transferQty;
      if (n > max) { showToast(`Closing cannot exceed ${max}`, "error"); return r; }
      return { ...r, closing: val };
    }));
  };

  /* ── Fetch detail modal data ── */
  const fetchTransactionData = async (rowId, type) => {
    try {
      const res = await apiClient.post(
        "/Liquor/GetDailyTransaction",
        { shopId, stockDate: date || todayISO }
      );
      if (res.data?.code === 200) {
        if (type === "received")
          setPopupData1(res.data.data?.trnReceived?.filter(i => i.productId === rowId) || []);
        else
          setPopupData2(res.data.data?.trnTransfer?.filter(i => i.productId === rowId) || []);
      }
    } catch (err) { console.error(err); }
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    const payload = rows
      .filter(r => r.closing !== "")
      .map(r => {
        const cl  = parseFloat(r.closing);
        const tot = r.openingStock + r.receivedQty - r.transferQty;
        const sl  = Math.max(0, tot - cl);
        return {
          productId:    r.id,
          categoryName:  r.cat,
          receivedStock: r.receivedQty,
          transferStock: r.transferQty,
          currentStock:  cl,
          soldStock:     sl,
          totalAmount:   sl * r.mrp,
          shopId,
          stockDate: date || todayISO,
        };
      });
    if (!payload.length) {
      showToast("Please enter closing stock for at least one product.", "error"); return;
    }
    try {
      setLoading(true);
      const res = await apiClient.post(
        "/Liquor/SaveProductData",
        payload
      );
      if (res.status === 200) {
        if (res.data?.data === "Negative stock detected after transaction.")
          showToast("Negative stock detected!", "error");
        else {
          showToast("Stock report submitted successfully!", "success");
          await checkStockData(); fetchStock();
        }
      }
    } catch (err) { showToast(getApiError(err, "Error submitting stock report!"), "error"); }
    finally   { setLoading(false); }
  };

  /* ── Helpers ── */
  const rowTotal = r => r.openingStock + r.receivedQty - r.transferQty;
  const rowSold  = r => { const cl = parseFloat(r.closing); return isNaN(cl) ? 0 : Math.max(0, rowTotal(r) - cl); };
  const rowValue = r => rowSold(r) * r.mrp;

  /* ── Derived ── */
  const filtered = rows.filter(r => {
    const matchCat  = activeCat === "ALL" || r.cat === activeCat;
    const matchSrch = r.pName.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSrch;
  });

  const uniqueCats  = [...new Set(rows.map(r => r.cat).filter(Boolean))];
  const filteredSegments = buildCategorySegments(filtered);

  const totalSold   = rows.reduce((s, r) => s + rowSold(r),  0);
  const totalValue  = rows.reduce((s, r) => s + rowValue(r), 0);
  const filledCount = rows.filter(r => r.closing !== "").length;
  const displayDate = new Date(date || todayISO).toLocaleDateString("en-GB");

  /* ── Group rows by pName within a catRows list ── */
  const buildGroups = catRows => {
    const order = []; const map = new Map();
    catRows.forEach(r => {
      if (!map.has(r.pName)) { map.set(r.pName, []); order.push(r.pName); }
      map.get(r.pName).push(r);
    });
    return order.map(name => ({ name, variants: map.get(name) }));
  };

  /* ── Select a variant chip ── */
  const selectVariant = (pName, id) =>
    setSelectedVars(prev => ({ ...prev, [pName]: id }));

  /* ─────────────────────────────────────────────── */
  return (
    <div style={{ background:"#fff", minHeight:"100%", fontFamily:"'Inter',-apple-system,sans-serif" }}>
      <ToastContainer />

      {/* PAGE HEADER */}
      <div style={{ padding:"22px 28px 10px" }}>
        <h1 style={{ margin:0, fontSize:21, fontWeight:800, color:P, letterSpacing:-0.3 }}>
          देशी पर्चा
        </h1>
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
          <span style={{ fontSize:12, color:MUT }}>Date: {displayDate}</span>
          <span style={{ fontSize:12, color:MUT }}>Shop: {shopName} &nbsp;|&nbsp; Time: {timeStr}</span>
        </div>
        <hr style={{ border:"none", borderTop:`1.5px solid ${BDR}`, margin:"10px 0 0" }}/>
      </div>

      {/* STAT CARDS */}
      <div style={{ padding:"12px 28px", display:"flex", gap:14, flexWrap:"wrap" }}>
        {[
          { label:"Total Products",    val: rows.length },
          { label:"Total Qty Sold",    val: totalSold },
          { label:"Total Sale Value",  val: fmt(totalValue) },
          { label:"Variants Filled",   val: `${filledCount} / ${rows.length}` },
        ].map(c => (
          <div key={c.label} style={{ flex:"1 1 160px", border:`1px solid ${BDR}`,
            borderRadius:10, padding:"12px 18px", background:"#fff" }}>
            <div style={{ fontSize:11, color:MUT, fontWeight:500, marginBottom:5 }}>{c.label}</div>
            <div style={{ fontSize:20, fontWeight:800, color:"#111827" }}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* TOOLBAR */}
      <div style={{ padding:"10px 28px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap",
        background:"#FAFAFA", borderTop:`1px solid ${BDR}`, borderBottom:`1px solid ${BDR}` }}>

        {/* Search */}
        <div style={{ position:"relative", flex:"1 1 180px", minWidth:140 }}>
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:MUT, fontSize:14 }}>🔍</span>
          <input type="text" placeholder="Search product…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width:"100%", paddingLeft:32, padding:"8px 12px 8px 32px",
              border:`1.5px solid ${BDR}`, borderRadius:7, fontSize:13, outline:"none", background:"#fff" }}/>
        </div>

        {/* Category tabs */}
        {uniqueCats.length > 0 && (
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {["ALL", ...uniqueCats].map(cat => (
              <button key={cat} onClick={() => setActiveCat(cat)} style={{
                padding:"5px 13px", borderRadius:20, fontSize:12, fontWeight:700,
                cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s",
                background: activeCat===cat ? P : "#fff",
                color:       activeCat===cat ? "#fff" : MUT,
                border:     `1.5px solid ${activeCat===cat ? P : BDR}`,
              }}>{cat}</button>
            ))}
          </div>
        )}

        <div style={{ flex:1 }}/>

        <button onClick={() => setPreview(true)} disabled={filledCount === 0} style={{
          display:"flex", alignItems:"center", gap:6,
          background: filledCount===0 ? "#F3F4F6" : "#FDF4F8",
          color:       filledCount===0 ? MUT : P,
          border:`1.5px solid ${filledCount===0 ? BDR : P+"44"}`,
          borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:700,
          cursor: filledCount===0 ? "not-allowed" : "pointer", whiteSpace:"nowrap",
        }}>👁 Preview</button>

        <button onClick={handleSubmit} disabled={loading} style={{
          display:"flex", alignItems:"center", gap:6,
          background: loading ? "#9CA3AF" : `linear-gradient(135deg,${PDK},${P})`,
          color:"#fff", border:"none", borderRadius:8,
          padding:"8px 20px", fontSize:13, fontWeight:700,
          cursor: loading ? "not-allowed" : "pointer",
          boxShadow:`0 4px 12px ${P}44`, whiteSpace:"nowrap",
        }}>
          {loading ? <><FaSpinner style={{ animation:"spin 1s linear infinite" }}/> Submitting…</> : "✅ Submit Report"}
        </button>
      </div>

      {/* STATES */}
      {apiConditionMet === null && (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
          justifyContent:"center", padding:"60px 0", gap:14, color:MUT }}>
          <FaSpinner style={{ fontSize:28, animation:"spin 1s linear infinite" }}/>
          <span style={{ fontSize:13, fontWeight:500 }}>Loading stock data…</span>
        </div>
      )}
      {apiConditionMet === false && (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
          justifyContent:"center", padding:"52px 24px", gap:12, textAlign:"center" }}>
          <div style={{ width:64, height:64, borderRadius:"50%", background:"#ECFDF5",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:28 }}>✅</div>
          <div style={{ fontWeight:800, fontSize:17, color:"#111827" }}>पर्चा जमा हो चुका है</div>
          <div style={{ fontSize:13, color:MUT }}>Today's stock report has already been submitted.</div>
        </div>
      )}

      {/* MAIN TABLE */}
      {apiConditionMet === true && (
        <div style={{ padding:"16px 28px 0" }}>
          <div style={{ border:`1px solid ${BDR}`, borderRadius:12,
            overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>

            {filteredSegments.map((seg, ci) => {
              const cat = seg.cat;
              const catRows = seg.rows;
              const groups   = buildGroups(catRows);
              const subSold  = catRows.reduce((s,r) => s + rowSold(r),  0);
              const subValue = catRows.reduce((s,r) => s + rowValue(r), 0);
              const showCat  = cat !== "";

              return (
                <div key={`${cat||"all"}-${ci}`}
                  style={{ borderBottom: ci<filteredSegments.length-1 ? `1px solid ${BDR}` : "none" }}>
                  <div style={{ display:"flex" }}>

                    {/* Rotated category label */}
                    {showCat && (
                      <div style={{ width:34, flexShrink:0, display:"flex",
                        alignItems:"center", justifyContent:"center",
                        borderRight:`1px solid ${BDR}`, background:"#fff" }}>
                        <span style={{ writingMode:"vertical-rl", transform:"rotate(180deg)",
                          fontSize:10, fontWeight:800, color:P,
                          letterSpacing:2.5, textTransform:"uppercase", userSelect:"none" }}>{cat}</span>
                      </div>
                    )}

                    <div style={{ flex:1, overflowX:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:860 }}>
                        <thead>
                          <tr style={{ background:P }}>
                            {["Product","Variant","Rate","Opening","Received","Transfer","Total","Closing","Sold","Value"].map(h => (
                              <th key={h} style={{ padding:"9px 12px", color:"#fff", fontWeight:600, fontSize:11,
                                textAlign:["Product","Variant"].includes(h) ? "left" : "right",
                                whiteSpace:"nowrap", borderRight:"1px solid rgba(255,255,255,0.10)" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* ONE ROW per product — variant chips switch the data */}
                          {groups.map(({ name, variants }, gi) => {
                            const selId  = selectedVars[name] ?? variants[0].id;
                            const r      = variants.find(v => v.id === selId) || variants[0];
                            const tot    = rowTotal(r);
                            const sl     = rowSold(r);
                            const vl     = rowValue(r);
                            const filled = r.closing !== "";
                            const filledVars = variants.filter(v => v.closing !== "").length;

                            return (
                              <tr key={name} style={{ background: filled ? "#FDF9FB" : "#fff",
                                borderTop: gi > 0 ? "2px solid #E5E7EB" : undefined }}>

                                {/* Product name */}
                                <td style={{ padding:"10px 14px", fontWeight:700, fontSize:13, color:"#111827",
                                  borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                  verticalAlign:"middle", background:"#FEFBFD", minWidth:160 }}>
                                  <div>{name}</div>
                                  {variants.length > 1 && (
                                    <div style={{ marginTop:5, fontSize:10, fontWeight:600,
                                      color: filledVars === variants.length ? GRN : MUT }}>
                                      {filledVars}/{variants.length} filled
                                    </div>
                                  )}
                                </td>

                                {/* Variant chips — click to reflect data in this row */}
                                <td style={{ padding:"8px 12px", verticalAlign:"middle",
                                  borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>
                                  <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                                    {variants.map(v => {
                                      const isActive = v.id === selId;
                                      const isDone   = v.closing !== "";
                                      return (
                                        <button key={v.id}
                                          onClick={() => selectVariant(name, v.id)}
                                          title={`Click to view ${v.packing} data`}
                                          style={{
                                            padding:"3px 10px", borderRadius:20, fontSize:12,
                                            fontWeight:700, cursor:"pointer", whiteSpace:"nowrap",
                                            transition:"all 0.15s",
                                            background: isActive ? P : isDone ? `${P}15` : "#F3F4F6",
                                            color:       isActive ? "#fff" : isDone ? P : "#374151",
                                            border:     `1.5px solid ${isActive ? P : isDone ? `${P}44` : BDR}`,
                                            boxShadow:   isActive ? `0 2px 6px ${P}44` : "none",
                                          }}>
                                          {v.packing}{isDone && !isActive ? " ✓" : ""}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </td>

                                {/* ── All data reflects selected variant ── */}
                                <NC>{fmt(r.mrp)}</NC>
                                <NC>{r.openingStock}</NC>

                                {/* Received — clickable */}
                                <td style={{ padding:"9px 10px", textAlign:"right",
                                  borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                  verticalAlign:"middle" }}>
                                  <button
                                    data-bs-toggle="modal" data-bs-target="#recModal"
                                    onClick={() => fetchTransactionData(r.id, "received")}
                                    style={{
                                      background: r.receivedQty>0 ? "#ECFDF5" : "#F9FAFB",
                                      color:      r.receivedQty>0 ? "#065F46" : MUT,
                                      border:`1px solid ${r.receivedQty>0 ? "#6EE7B7" : BDR}`,
                                      borderRadius:20, padding:"3px 10px",
                                      fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                                    {r.receivedQty > 0 ? `⬇ ${r.receivedQty}` : "—"}
                                  </button>
                                </td>

                                {/* Transfer — clickable */}
                                <td style={{ padding:"9px 10px", textAlign:"right",
                                  borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                  verticalAlign:"middle" }}>
                                  <button
                                    data-bs-toggle="modal" data-bs-target="#trnModal"
                                    onClick={() => fetchTransactionData(r.id, "transfer")}
                                    style={{
                                      background: r.transferQty>0 ? "#FFF7ED" : "#F9FAFB",
                                      color:      r.transferQty>0 ? "#C2410C" : MUT,
                                      border:`1px solid ${r.transferQty>0 ? "#FED7AA" : BDR}`,
                                      borderRadius:20, padding:"3px 10px",
                                      fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                                    {r.transferQty > 0 ? `⬆ ${r.transferQty}` : "—"}
                                  </button>
                                </td>

                                <NC bold>{tot}</NC>

                                {/* Closing input — saves to selected variant's productId */}
                                <td style={{ padding:"5px 8px", textAlign:"center",
                                  borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                  verticalAlign:"middle" }}>
                                  <input type="number" min="0"
                                    value={r.closing}
                                    onChange={e => handleClosing(r.id, e.target.value)}
                                    placeholder={String(tot)}
                                    style={{ width:70, textAlign:"center",
                                      border:`1.5px solid ${filled ? P : BDR}`,
                                      borderRadius:6, padding:"5px 6px",
                                      fontSize:13, fontWeight:filled?700:400,
                                      color:filled?P:"#374151",
                                      background:filled?"#FDF4F8":"#fff", outline:"none" }}/>
                                </td>

                                <NC color={sl>0?"#111827":MUT} bold={sl>0}>{sl}</NC>
                                <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:600,
                                  color:vl>0?"#111827":MUT, borderBottom:`1px solid ${BDR}` }}>
                                  {fmt(vl)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* Category subtotal */}
                      {showCat && (
                        <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center",
                          padding:"7px 16px", background:"#FDF4F8",
                          borderTop:`1px solid ${BDR}`, gap:20 }}>
                          <span style={{ fontSize:12, color:MUT }}>
                            Sold: <b style={{ color:"#111827" }}>{subSold}</b>
                          </span>
                          <span style={{ fontSize:12, fontWeight:700, color:MUT }}>{cat} Subtotal:</span>
                          <span style={{ fontSize:14, fontWeight:800, color:P, minWidth:80, textAlign:"right" }}>
                            {fmt(subValue)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Grand total */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"16px 28px", background:"#FDF4F8", borderTop:`2px solid ${P}22` }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>Grand Total Sale Amount</div>
                <div style={{ fontSize:12, color:MUT, marginTop:2 }}>Total Quantity Sold: {totalSold} units</div>
              </div>
              <div style={{ fontSize:26, fontWeight:900, color:P }}>{fmt(totalValue)}</div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PREVIEW MODAL ════════════════════════════ */}
      {preview && (
        <div style={{ position:"fixed", inset:0, zIndex:9999,
          background:"rgba(15,23,42,0.6)", backdropFilter:"blur(4px)",
          display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:"24px 16px", overflowY:"auto" }}>
          <div style={{ background:"#fff", borderRadius:14, width:"100%", maxWidth:1100,
            boxShadow:"0 24px 64px rgba(0,0,0,0.25)" }}>

            <div style={{ padding:"16px 24px", borderBottom:`1px solid ${BDR}`,
              display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:P }}>👁 पर्चा Preview</div>
                <div style={{ fontSize:12, color:MUT, marginTop:2 }}>
                  Date: {displayDate} &nbsp;|&nbsp; Filled: {filledCount} of {rows.length}
                </div>
              </div>
              <button onClick={() => setPreview(false)} style={{
                background:"#F3F4F6", border:`1px solid ${BDR}`, borderRadius:7,
                padding:"7px 16px", fontSize:12, fontWeight:600, cursor:"pointer", color:"#374151" }}>
                ✕ Close
              </button>
            </div>

            <div style={{ padding:"12px 24px 0", display:"flex", gap:12, flexWrap:"wrap" }}>
              {[
                { label:"Total Products", val: rows.length },
                { label:"Qty Sold",       val: totalSold },
                { label:"Sale Value",     val: fmt(totalValue) },
                { label:"Filled",         val: `${filledCount}/${rows.length}` },
              ].map(c => (
                <div key={c.label} style={{ flex:"1 1 120px", border:`1px solid ${BDR}`,
                  borderRadius:8, padding:"10px 14px", background:"#FAFAFA" }}>
                  <div style={{ fontSize:10, color:MUT, fontWeight:600, marginBottom:3 }}>{c.label}</div>
                  <div style={{ fontSize:17, fontWeight:800, color:"#111827" }}>{c.val}</div>
                </div>
              ))}
            </div>

            {/* Preview — all products grouped by category, all variants as rows */}
            <div style={{ padding:"16px 24px" }}>
              <div style={{ border:`1px solid ${BDR}`, borderRadius:10, overflow:"hidden" }}>
                {(() => {
                  const pvSegments = buildCategorySegments(rows);
                  return pvSegments.map((seg, ci) => {
                    const cat = seg.cat;
                    const catRows = seg.rows;
                    const groups  = buildGroups(catRows);
                    const subVal  = catRows.reduce((s,r)=>s+rowValue(r),0);
                    const subSold = catRows.reduce((s,r)=>s+rowSold(r),0);
                    return (
                      <div key={`${cat||"all"}-${ci}`}
                        style={{ borderBottom:ci<pvSegments.length-1?`1px solid ${BDR}`:"none" }}>
                        <div style={{ display:"flex" }}>
                          {cat !== "" && (
                            <div style={{ width:30, flexShrink:0, display:"flex",
                              alignItems:"center", justifyContent:"center",
                              borderRight:`1px solid ${BDR}`, background:"#fff" }}>
                              <span style={{ writingMode:"vertical-rl", transform:"rotate(180deg)",
                                fontSize:9, fontWeight:800, color:P,
                                letterSpacing:2, textTransform:"uppercase", userSelect:"none" }}>{cat}</span>
                            </div>
                          )}
                          <div style={{ flex:1, overflowX:"auto" }}>
                            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, minWidth:700 }}>
                              <thead>
                                <tr style={{ background:P }}>
                                  {["Product","Vol","Rate","Opening","Received","Transfer","Total","Closing","Sold","Value"].map(h => (
                                    <th key={h} style={{ padding:"7px 10px", color:"#fff", fontWeight:600, fontSize:10,
                                      textAlign:["Product","Vol"].includes(h)?"left":"right",
                                      borderRight:"1px solid rgba(255,255,255,0.10)", whiteSpace:"nowrap" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {/* Preview shows ALL variants as separate rows with rowSpan */}
                                {groups.flatMap(({ name, variants }) =>
                                  variants.map((r, vi) => {
                                    const isFirst = vi === 0;
                                    const tot = rowTotal(r), sl = rowSold(r), vl = rowValue(r);
                                    const filled = r.closing !== "";
                                    const topBdr = isFirst ? "2px solid #D1D5DB" : `1px solid ${BDR}`;
                                    return (
                                      <tr key={r.id} style={{ background:filled?"#FDF9FB":"#fff" }}>
                                        {isFirst && (
                                          <td rowSpan={variants.length} style={{ padding:"8px 12px",
                                            fontWeight:700, color:"#111827", fontSize:12,
                                            borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                            borderTop:topBdr, verticalAlign:"middle",
                                            background:"#FEFBFD", minWidth:130 }}>{name}</td>
                                        )}
                                        <td style={{ padding:"7px 10px",
                                          borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                          borderTop:isFirst?topBdr:undefined }}>
                                          <span style={{ background:"#F3F4F6", borderRadius:4,
                                            padding:"1px 6px", fontSize:10, fontWeight:600, color:"#374151" }}>
                                            {r.packing}
                                          </span>
                                        </td>
                                        <SNC top={isFirst?topBdr:undefined}>{fmt(r.mrp)}</SNC>
                                        <SNC top={isFirst?topBdr:undefined}>{r.openingStock}</SNC>
                                        <SNC top={isFirst?topBdr:undefined} color={GRN} bold>{r.receivedQty}</SNC>
                                        <SNC top={isFirst?topBdr:undefined} color={ORG} bold>{r.transferQty}</SNC>
                                        <SNC top={isFirst?topBdr:undefined} bold>{tot}</SNC>
                                        <td style={{ padding:"7px 10px", textAlign:"center",
                                          fontWeight:700, color:filled?P:MUT,
                                          borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                          borderTop:isFirst?topBdr:undefined }}>
                                          {filled ? r.closing : <span style={{color:"#D1D5DB"}}>—</span>}
                                        </td>
                                        <SNC top={isFirst?topBdr:undefined} color={sl>0?"#111827":MUT} bold={sl>0}>{sl}</SNC>
                                        <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:600,
                                          color:vl>0?"#111827":MUT,
                                          borderBottom:`1px solid ${BDR}`, borderTop:isFirst?topBdr:undefined }}>
                                          {fmt(vl)}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                            {cat !== "" && (
                              <div style={{ display:"flex", justifyContent:"flex-end",
                                padding:"6px 14px", background:"#FDF4F8",
                                borderTop:`1px solid ${BDR}`, gap:16 }}>
                                <span style={{ fontSize:11, color:MUT }}>Sold: <b>{subSold}</b></span>
                                <span style={{ fontSize:11, fontWeight:700, color:MUT }}>{cat} Subtotal:</span>
                                <span style={{ fontSize:13, fontWeight:800, color:P, minWidth:70, textAlign:"right" }}>{fmt(subVal)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"14px 22px", background:"#FDF4F8", borderTop:`2px solid ${P}22` }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>Grand Total Sale Amount</div>
                    <div style={{ fontSize:11, color:MUT, marginTop:2 }}>Qty Sold: {totalSold} units</div>
                  </div>
                  <div style={{ fontSize:22, fontWeight:900, color:P }}>{fmt(totalValue)}</div>
                </div>
              </div>

              {/* Category summary */}
              {uniqueCats.length > 0 && (
                <div style={{ marginTop:16, border:`1px solid ${BDR}`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"9px 14px", background:P, fontSize:11, fontWeight:700, color:"#fff", letterSpacing:0.5 }}>
                    CATEGORY-WISE SUMMARY
                  </div>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                    <thead>
                      <tr style={{ background:"#F8F8F8" }}>
                        {["Category","Variants","Qty Sold","Sale Value"].map(h => (
                          <th key={h} style={{ padding:"8px 14px", fontSize:11, fontWeight:700, color:MUT,
                            textAlign:["Category","Variants"].includes(h)?"left":"right",
                            borderBottom:`1px solid ${BDR}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uniqueCats.map((cat, i) => {
                        const cr   = rows.filter(r=>r.cat===cat);
                        const cSld = cr.reduce((s,r)=>s+rowSold(r),0);
                        const cVal = cr.reduce((s,r)=>s+rowValue(r),0);
                        return (
                          <tr key={cat} style={{ background:i%2===0?"#fff":"#FAFAFA" }}>
                            <td style={{ padding:"9px 14px", fontWeight:700, color:P, borderBottom:`1px solid ${BDR}` }}>{cat}</td>
                            <td style={{ padding:"9px 14px", color:"#374151", borderBottom:`1px solid ${BDR}` }}>{cr.length} variants</td>
                            <td style={{ padding:"9px 14px", textAlign:"right", fontWeight:600, color:"#111827", borderBottom:`1px solid ${BDR}` }}>{cSld}</td>
                            <td style={{ padding:"9px 14px", textAlign:"right", fontWeight:700, color:cVal>0?"#059669":MUT, borderBottom:`1px solid ${BDR}` }}>{fmt(cVal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:"#FDF4F8" }}>
                        <td colSpan={2} style={{ padding:"10px 14px", fontWeight:800, color:"#111827" }}>Grand Total</td>
                        <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:800, color:"#111827" }}>{totalSold}</td>
                        <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:900, color:P, fontSize:15 }}>{fmt(totalValue)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div style={{ padding:"14px 24px", borderTop:`1px solid ${BDR}`,
              display:"flex", justifyContent:"flex-end", gap:10,
              background:"#FAFAFA", borderRadius:"0 0 14px 14px" }}>
              <button onClick={() => setPreview(false)} style={{
                background:"#F3F4F6", color:"#374151", border:`1px solid ${BDR}`,
                borderRadius:8, padding:"9px 22px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                Close
              </button>
              <button onClick={() => { setPreview(false); handleSubmit(); }} style={{
                background:`linear-gradient(135deg,${PDK},${P})`, color:"#fff", border:"none",
                borderRadius:8, padding:"9px 24px", fontSize:13, fontWeight:700, cursor:"pointer",
                boxShadow:`0 4px 12px ${P}44` }}>
                ✅ Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RECEIVED DETAIL MODAL */}
      <div className="modal fade" id="recModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ color:P, fontWeight:700 }}>⬇ Received Stock Details</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"/>
            </div>
            <div className="modal-body">
              {popupData1.length > 0 ? (
                <table className="table table-bordered table-striped">
                  <thead><tr><th>Product</th><th>Receive Type</th><th>Details</th><th>Qty</th></tr></thead>
                  <tbody>
                    {popupData1.map((item, i) => (
                      <tr key={item.id || i}>
                        <td>{item.productName || "N/A"}</td>
                        <td>{item.receivedHead || "N/A"}</td>
                        <td>{item.receivedHead==="RSBCL"?`Invoice: ${item.invoiceNo||"N/A"}`:
                          item.receivedHeadType==="SHOP"||item.receivedHeadType==="SHOP TO SHOP"?
                          `Shop: ${item.selectedShop||"N/A"}`:`Party: ${item.receivedHeadName||"N/A"}`}</td>
                        <td style={{ fontWeight:700 }}>{item.qty || "0"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <EmptyState message="No received stock data" />}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* TRANSFER DETAIL MODAL */}
      <div className="modal fade" id="trnModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ color:P, fontWeight:700 }}>⬆ Transfer Stock Details</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"/>
            </div>
            <div className="modal-body">
              {popupData2.length > 0 ? (
                <table className="table table-bordered table-striped">
                  <thead><tr><th>Product</th><th>Transfer Head</th><th>Detail</th><th>Qty</th></tr></thead>
                  <tbody>
                    {popupData2.map((item, i) => (
                      <tr key={item.id || i}>
                        <td>{item.productName || "N/A"}</td>
                        <td>{item.transferHead || "N/A"}</td>
                        <td>{item.invoiceNo || item.transferPartyName || item.transferShopName || "N/A"}</td>
                        <td style={{ fontWeight:700 }}>{item.qty || "0"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <EmptyState message="No transfer data" />}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function NC({ children, color, bold }) {
  return (
    <td style={{ padding:"9px 12px", textAlign:"right",
      color:color||"#374151", fontWeight:bold?700:500,
      borderBottom:"1px solid #E5E7EB", borderRight:"1px solid #E5E7EB",
      verticalAlign:"middle" }}>{children}</td>
  );
}
function SNC({ children, top, color, bold }) {
  return (
    <td style={{ padding:"7px 10px", textAlign:"right",
      color:color||"#374151", fontWeight:bold?700:500,
      borderBottom:"1px solid #E5E7EB", borderRight:"1px solid #E5E7EB",
      borderTop:top }}>{children}</td>
  );
}
